package org.moxultimate.topwithmox.components.navbar

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_GAME_APP_TOGGLE
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_PRODUCT_TOGGLE
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_SHOWS_TOGGLE
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_TECH_TOGGLE
import org.moxultimate.topwithmox.navigation.NavRoute
import org.moxultimate.topwithmox.theme.DarkGrayColor
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily

/** Light panel background. */
private val NavLightBG = Color.rgb(0xFF, 0xFF, 0xFF)

/** Subtle dark divider line used between nav items. */
private val DividerColor = Color.rgba(0x00, 0x00, 0x00, 0x0C)

/** Mox rainbow gradient CSS value — reused for the accent stripe and active indicator. */
private const val MoxGradientCss =
    "linear-gradient(to right, #f47b20, #e61a26, #8b3a8a, #1d71b8, #46b7e6)"

/**
 * SideNavBar
 * Mobile slide-in navigation drawer with a dark theme matching the dashboard.
 * A dim overlay covers the rest of the screen; tapping it closes the drawer.
 *
 * @param menuState     Current open/closing state.
 * @param close         Called to begin the close animation.
 * @param onAnimationEnd Called when the slide animation ends (fully hides the drawer).
 */
@Composable
fun SideNavBar(menuState: SideNavBarState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    val ctx = rememberPageContext()
    val currentPath = ctx.route.path

    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, Color.rgba(0, 0, 0, 0.55f))
            .zIndex(200)
            .onClick { close() }
    ) {
        key(menuState) {
            Column(
                modifier = Modifier
                    .fillMaxHeight()
                    .width(75.vw)
                    .align(Alignment.CenterEnd)
                    .backgroundColor(NavLightBG)
                    .borderRadius(topLeft = 24.px, bottomLeft = 24.px)
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 220.ms,
                            timingFunction = if (menuState == SideNavBarState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                            direction = if (menuState == SideNavBarState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                            fillMode = AnimationFillMode.Forwards,
                        )
                    )
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() }
                    .styleModifier {
                        property("overflow", "hidden")
                        property("box-shadow", "-8px 0 32px rgba(0,0,0,0.15)")
                    },
            ) {
                // Mox rainbow accent stripe at the very top
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(3.px)
                        .styleModifier {
                            property("background", MoxGradientCss)
                            property("flex-shrink", "0")
                        }
                )

                // Header row: logo + close button
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 20.px, topBottom = 18.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Image(
                        src = "/logo_topwithmox.webp",
                        description = "Top With Mox",
                        modifier = Modifier
                            .height(2.2.cssRem)
                            .styleModifier { property("display", "block") },
                    )

                    Box(modifier = Modifier.flexGrow(1))

                    NavCloseButton(onClick = { close() })
                }

                // Header separator
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(DividerColor)
                )

                // Nav links
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 8.px, bottom = 24.px),
                ) {
                    SideNavMenuItems(currentPath = currentPath, onClose = close)
                }
            }
        }
    }
}

/**
 * NavCloseButton
 * Circular X button in the header. Lights up subtly on hover.
 *
 * @param onClick Callback when tapped.
 */
@Composable
private fun NavCloseButton(onClick: () -> Unit) {
    var isHovered by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .size(36.px)
            .borderRadius(50.percent)
            .backgroundColor(
                if (isHovered) Color.rgba(0x00, 0x00, 0x00, 0x0C) else Colors.Transparent
            )
            .styleModifier {
                property("cursor", "pointer")
                property("transition", "background-color 0.2s ease")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                onClick { onClick() }
            },
        contentAlignment = Alignment.Center,
    ) {
        Image(
            src = "/ic_close.png",
            description = "Close",
            modifier = Modifier
                .size(18.px)
                .styleModifier { property("display", "block") },
        )
    }
}

/**
 * SideNavMenuItems
 * Ordered list of nav rows. Mirror any additions in [NavBarMenuItems] inside NavBar.kt.
 *
 * @param currentPath Active browser route for active-state highlighting.
 * @param onClose     Called after a link tap to close the drawer.
 */
@Composable
private fun SideNavMenuItems(currentPath: String, onClose: () -> Unit) {
    SideNavItem(NavRoute.DASHBOARD, "Home", currentPath, onClose)
    NavDivider()
    SideNavItem(NavRoute.WALLPAPER, "Top Wallpapers", currentPath, onClose)
    NavDivider()
    SideNavItem(NavRoute.APPS, "Top Games & Apps", currentPath, onClose, disabled = !DASHBOARD_GAME_APP_TOGGLE)
    NavDivider()
    SideNavItem(NavRoute.SHOWS, "Top Shows", currentPath, onClose, disabled = !DASHBOARD_SHOWS_TOGGLE)
    NavDivider()
    SideNavItem(NavRoute.TECH, "Top Tech", currentPath, onClose, disabled = !DASHBOARD_TECH_TOGGLE)
    NavDivider()
    SideNavItem(NavRoute.PRODUCTS, "Top Products", currentPath, onClose, disabled = !DASHBOARD_PRODUCT_TOGGLE)
    NavDivider()
    SideNavItem(NavRoute.ABOUT, "About", currentPath, onClose)
}

/**
 * SideNavItem
 * Single navigation row. Shows a gradient left-bar and gradient text when active;
 * gradient text also appears on hover. Disabled items render as muted gray and are inert.
 *
 * @param path        Route to navigate to.
 * @param label       Display text.
 * @param currentPath Current browser path for active detection.
 * @param onClose     Called when the link is tapped so the drawer closes.
 * @param disabled    Renders as non-interactive muted text when true.
 */
@Composable
private fun SideNavItem(
    path: String,
    label: String,
    currentPath: String,
    onClose: () -> Unit,
    disabled: Boolean = false,
) {
    val isActive = if (path == NavRoute.DASHBOARD) currentPath == "/" else currentPath.startsWith(path)
    var isHovered by remember { mutableStateOf(false) }
    val showGradient = (isActive || isHovered) && !disabled

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 20.px, topBottom = 14.px)
            .backgroundColor(
                when {
                    isActive -> Color.rgba(0x00, 0x00, 0x00, 0x06)
                    isHovered && !disabled -> Color.rgba(0x00, 0x00, 0x00, 0x04)
                    else -> Colors.Transparent
                }
            )
            .styleModifier {
                property("transition", "background-color 0.15s ease")
                if (!disabled) property("cursor", "pointer")
            }
            .attrsModifier {
                if (!disabled) {
                    onMouseEnter { isHovered = true }
                    onMouseLeave { isHovered = false }
                }
            },
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth(),
        ) {
            // Active indicator — vertical gradient bar on the left edge
            Box(
                modifier = Modifier
                    .width(3.px)
                    .height(20.px)
                    .borderRadius(2.px)
                    .margin(right = 14.px)
                    .styleModifier {
                        property(
                            "background",
                            if (isActive) "linear-gradient(to bottom, #f47b20, #e61a26, #8b3a8a)"
                            else "transparent"
                        )
                    }
            )

            when {
                disabled -> SpanText(
                    text = label,
                    modifier = Modifier
                        .color(DarkGrayColor)
                        .fontFamily(FontFamily)
                        .fontSize(15.px)
                        .fontWeight(FontWeight.Normal),
                )

                else -> Link(
                    path = path,
                    text = label,
                    modifier = Modifier
                        .then(if (showGradient) Modifier.moxTextGradient() else Modifier.color(DarkTextColor))
                        .fontFamily(FontFamily)
                        .fontSize(15.px)
                        .fontWeight(if (isActive) FontWeight.SemiBold else FontWeight.Normal)
                        .attrsModifier { onClick { onClose() } },
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                )
            }
        }
    }
}

/**
 * NavDivider
 * 1px translucent white separator between nav items.
 */
@Composable
private fun NavDivider() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.px)
            .backgroundColor(DividerColor)
    )
}

val SideMenuSlideInAnim = Keyframes {
    from { Modifier.translateX(100.percent) }
    to { Modifier }
}
